import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * Integritetspolicy (GDPR) för vidalv.live.
 * Granskad utifrån GDPR (EU 2016/679) + svensk dataskyddslag (2018:218).
 */
export default function Integritetspolicy() {
  useEffect(() => {
    document.title = "Integritetspolicy – vidalv.live";
    window.scrollTo(0, 0);
  }, []);

  return (
    <article data-testid="legal-integritetspolicy" className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14 prose prose-sm sm:prose-base">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-pine hover:text-pine-hover no-underline mb-6">
        <ArrowLeft size={14} /> Tillbaka till startsidan
      </Link>
      <h1 className="font-fraktur-smooth text-falu text-3xl sm:text-4xl mb-2">Integritetspolicy</h1>
      <p className="text-xs text-bark/60 mb-8">Senast uppdaterad: 16 maj 2026</p>

      <section className="space-y-4 text-ink leading-relaxed">
        <p>
          Vi värnar om din personliga integritet. I denna integritetspolicy beskriver vi hur vi på
          <strong> vidalv.live</strong> behandlar dina personuppgifter i enlighet med EU:s
          dataskyddsförordning (GDPR) och svensk dataskyddslagstiftning.
        </p>

        <h2 className="font-heading text-xl font-semibold text-ink mt-6 mb-2">1. Personuppgiftsansvarig</h2>
        <p>
          <strong>Hodema Edertools AB</strong><br />
          Organisationsnummer: 556887-7699<br />
          Kyrkvägen 66, 883 72 Backe, Sverige<br />
          E-post: <a href="mailto:redaktion@vidalv.live" className="text-falu">redaktion@vidalv.live</a><br />
          Telefon: +46 70 366 09 81
        </p>

        <h2 className="font-heading text-xl font-semibold text-ink mt-6 mb-2">2. Vilka personuppgifter vi samlar in</h2>
        <p>Vi samlar in och behandlar följande uppgifter:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>När du skickar in ett anslag, evenemang, foto eller ändringsförslag:</strong> namn, e-postadress, eventuellt telefonnummer, ort och det innehåll du lämnar in.</li>
          <li><strong>När du loggar in som moderator:</strong> e-postadress, lösenord (krypterat), roll (moderator/master).</li>
          <li><strong>När du besöker sajten:</strong> tekniska loggar (IP-adress, webbläsartyp, besökta sidor) i upp till 30 dagar för säkerhetsändamål.</li>
          <li><strong>Besöksstatistik (cookielöst server-hash-läge):</strong> Vi kör <strong>PostHog</strong> i EU-molnet med aktiverat <em>cookieless server-hash-läge</em>. PostHog sätter inga cookies och skriver inget i localStorage/sessionStorage. På serversidan hashas vissa tekniska signaler (bl.a. IP + user-agent) till ett kortlivat, dygnsvis rullande ID enbart för att särskilja unika besök. Vi skapar inga personprofiler och gör ingen identifiering. Endast sidvisningar och Web Vitals loggas (läs mer i <Link to="/cookiepolicy" className="text-falu">Cookiepolicyn</Link>).</li>
        </ul>

        <h2 className="font-heading text-xl font-semibold text-ink mt-6 mb-2">3. Ändamål och rättslig grund</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Publicering av inskickat innehåll:</strong> Fullgörande av din förfrågan (art. 6.1.b GDPR).</li>
          <li><strong>Moderatorkonton och säkerhet:</strong> Berättigat intresse att driva och skydda tjänsten (art. 6.1.f GDPR).</li>
          <li><strong>E-postnotifieringar till redaktionen:</strong> Berättigat intresse för redaktionell kvalitet (art. 6.1.f GDPR).</li>
          <li><strong>Besöksstatistik (server-hash-läge):</strong> Berättigat intresse att förstå hur tjänsten används och drivs (art. 6.1.f GDPR). PostHog körs cookielöst och utan personprofiler – IP och user-agent behandlas endast pseudonymiserat via en kortlivad server-side-hash.</li>
        </ul>

        <h2 className="font-heading text-xl font-semibold text-ink mt-6 mb-2">4. Mottagare av uppgifter</h2>
        <p>
          Vi delar uppgifter endast med följande personuppgiftsbiträden, under sekretessavtal och för
          de angivna ändamålen:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Emergent.sh</strong> – hosting av webbplatsen och databasen (EU/USA, Standard Contractual Clauses tillämpas).</li>
          <li><strong>Resend Inc.</strong> – utskick av transaktionella e-postmeddelanden till redaktionen (EU/USA, SCC).</li>
          <li><strong>PostHog Inc.</strong> – besöksstatistik i EU Cloud (Frankfurt) med cookieless server-hash-läge. Ingen personprofil, ingen identifiering, inga cookies eller storage-nycklar sätts i webbläsaren.</li>
          <li><strong>Cloudflare R2</strong> – objektlagring för bilder och bilagor (EU-jurisdiction).</li>
          <li><strong>SMHI, OpenStreetMap/Nominatim</strong> – externa data-API:er (ingen persondata skickas).</li>
        </ul>
        <p>Vi säljer aldrig dina uppgifter.</p>

        <h2 className="font-heading text-xl font-semibold text-ink mt-6 mb-2">5. Lagringstid</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Publicerade anslag:</strong> så länge de är aktuella, därefter arkiveras i &bdquo;Tidigare anslag&rdquo; i upp till 3 år.</li>
          <li><strong>Avvisade bidrag:</strong> raderas inom 60 dagar.</li>
          <li><strong>Moderator-konton:</strong> så länge rollen är aktiv, raderas inom 30 dagar efter avslut.</li>
          <li><strong>Tekniska loggar:</strong> max 30 dagar.</li>
          <li><strong>Cookies:</strong> se <Link to="/cookiepolicy" className="text-falu">Cookiepolicy</Link>.</li>
        </ul>

        <h2 className="font-heading text-xl font-semibold text-ink mt-6 mb-2">6. Dina rättigheter</h2>
        <p>Enligt GDPR har du rätt att:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>begära ett <strong>registerutdrag</strong> över vilka uppgifter vi har om dig,</li>
          <li>få felaktiga uppgifter <strong>rättade</strong>,</li>
          <li>begära <strong>radering</strong> (&bdquo;rätten att bli glömd&rdquo;),</li>
          <li>begära <strong>begränsning</strong> eller invända mot behandling,</li>
          <li>få dina uppgifter i <strong>portabelt format</strong>,</li>
          <li>när som helst <strong>återkalla samtycke</strong>,</li>
          <li>klaga hos tillsynsmyndigheten <a href="https://www.imy.se" target="_blank" rel="noopener noreferrer" className="text-falu">Integritetsskyddsmyndigheten (IMY)</a>.</li>
        </ul>
        <p>
          Skicka dina förfrågningar till <a href="mailto:redaktion@vidalv.live" className="text-falu">redaktion@vidalv.live</a>.
          Vi svarar inom 30 dagar.
        </p>

        <h2 className="font-heading text-xl font-semibold text-ink mt-6 mb-2">7. Överföringar utanför EU/EES</h2>
        <p>
          Vissa tjänster (hosting, e-post) kan innebära överföring till USA. I sådana fall använder vi
          EU-kommissionens <em>Standard Contractual Clauses</em> samt tekniska skyddsåtgärder (kryptering).
        </p>

        <h2 className="font-heading text-xl font-semibold text-ink mt-6 mb-2">8. Säkerhet</h2>
        <p>
          Vi skyddar dina uppgifter med tekniska och organisatoriska åtgärder: HTTPS/TLS-kryptering,
          lösenords-hashning (bcrypt), JWT-tokens, åtkomstbegränsningar, loggning och regelbundna säkerhetsgranskningar.
        </p>

        <h2 className="font-heading text-xl font-semibold text-ink mt-6 mb-2">9. Barn och unga</h2>
        <p>
          Tjänsten riktar sig till alla i bygden. Om du är under 13 år bör en vårdnadshavare godkänna att
          du skickar in innehåll. Vi publicerar aldrig bilder på identifierbara barn utan föräldrars samtycke.
        </p>

        <h2 className="font-heading text-xl font-semibold text-ink mt-6 mb-2">10. Ändringar</h2>
        <p>
          Vi uppdaterar denna policy vid behov. Väsentliga ändringar aviseras på startsidan.
        </p>
      </section>
    </article>
  );
}
